import merge from 'lodash/merge';
import {createTheme, responsiveFontSizes, ThemeOptions} from '@mui/material/styles';
import type { Direction, Theme } from '@mui/material';
import { THEMES } from '../constants';
import { lightShadows } from './shadows';

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

const baseOptions: ThemeOptions = {
  direction: 'ltr',
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box'
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%'
        },
        body: {
          height: '100%'
        },
        '#root': {
          height: '100%'
        },
        '#nprogress .bar': {
          zIndex: '2000 !important'
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  },
  typography: {
    button: {
      fontWeight: 600
    },
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem'
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem'
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem'
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem'
    },
    overline: {
      fontWeight: 600
    }
  }
};

const themesOptions: Record<string, ThemeOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e'
            }
          }
        }
      }
    },
    palette: {
      action: {
        active: '#6b778c'
      },
      background: {
        default: '#f4f5f7',
        paper: '#ffffff'
      },
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      mode: 'light',
      primary: {
        contrastText: '#ffffff',
        main: '#334e82'
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#FFA834'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#3b4754',
        secondary: '#FFA834',
      },
      warning: {
        contrastText: '#ffffff',
        main: '#FFA834'
      }
    },
    shadows: lightShadows
  }
};

export const createCustomTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
      merge(
          {},
          baseOptions,
          themeOptions,
          customOverrides,
          {
            ...(
                config.roundedCorners && {
                  shape: {
                    borderRadius: 16
                  }
                }
            )
          },
          {
            direction: config.direction
          }
      )
  );
  loadThemeAssets(theme); // <-- carica i font

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
const loadedFonts: Record<string, boolean> = {};

function loadThemeAssets(themeObject: object): object {
  function addGoogleLink(font: string) {
    const cleanFont = font.split(',')[0].trim().replace(/['"]/g, ''); // Rimuove virgolette e fallback
    const fontParam = cleanFont.replace(/\s+/g, '+');
    const url = `https://fonts.googleapis.com/css2?family=${fontParam}&display=swap`;

    if (loadedFonts[url]) return;

    const link = document.createElement('link');
    link.href = url;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    loadedFonts[url] = true;
  }

  function findFontFamilies(obj: any): string[] {
    const foundFonts = new Set<string>();

    function traverse(obj: any) {
      if (typeof obj !== 'object' || obj === null) return;

      if ('fontFamily' in obj && typeof obj.fontFamily === 'string') {
        foundFonts.add(obj.fontFamily);
      }

      for (const key in obj) {
        traverse(obj[key]);
      }
    }

    traverse(obj);
    return Array.from(foundFonts);
  }

  const fonts = findFontFamilies(themeObject);
  fonts.forEach(addGoogleLink);

  return themeObject;
}

const customOverrides: ThemeOptions = {
  palette: {
    primary: {
      main: '#FFA834',
      light: 'rgb(237, 240, 244)',
      dark: 'rgb(96, 96, 96)',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#FFA834', //questo era scuro, si vede che nei bottoni si usa il secondary
      contrastText: '#ffffff',
      light: 'rgb(237, 240, 244)',
    },
    action: {
      active: '#6b778c'
    },
    background: {
      default: '#f4f5f7',
      paper: '#ffffff'
    },
    error: {
      contrastText: '#ffffff',
      main: '#f44336'
    },
    mode: 'light',
    success: {
      contrastText: '#ffffff',
      main: '#4caf50'
    },
    text: {
      primary: '#3b4754',
      secondary: '#ff9e00',
      disabled: '#798da3'
    },
    warning: {
      contrastText: '#ffffff',
      main: '#ff9800'
    }
  },
  typography: {
    fontFamily: 'Open Sans'
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          '&:hover': { backgroundColor: '#FFA834',
            color: '#ffffff',}
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid #FFA834',
          borderRadius: '4px',
          margin: '6px',
          height: '50px',
          minHeight: '50px',
          minWidth: '120px',
          padding: '6px 12px',
          color: '#FFA834',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&.Mui-selected': {
            backgroundColor: '#FFA834',
            color: '#ffffff',
            boxShadow: 'none'
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgb(220, 140, 30)'
          },
          '&:hover': { backgroundColor: 'rgba(255, 168, 52, 0.1)' },
          '&.Mui-disabled': {
            color: 'rgba(0,0,0,0.3)',
            borderColor: 'rgba(0,0,0,0.1)'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          width: '90%',
          maxWidth: '90%',
          padding: '4px',
          '& .MuiTabs-flexContainer': { justifyContent: 'center' }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          transition: 'background-color 0.3s ease',
          '&.Mui-focused': { backgroundColor: '#ffffff' }
        },
        input: { padding: '12px' }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
          '@media (max-width:600px)': { fontSize: '0.7rem' }
        }
      }
    }
  }
};
